import { useCallback, useEffect, useRef, useState } from "react";
import { useOrderContext } from "@/context/order-context";
import type { Order } from "@/schemas/order";

type Direction = "next" | "prev";

interface PendingOrderMove {
  direction: Direction;
  /** order the move starts from */
  fromId: string;
  /** page the neighbour is on */
  page: number;
  /** index on that page used when the start order is not on it: a counted position, or the page's first / last */
  fallbackIndex: number | "first" | "last";
  /** when the start order's position was counted: the page must be loaded after it (ms epoch, 0 = not counted) */
  since: number;
}

/**
 * Previous / next order of the loaded orders list (filters, sort and paging of OrderContext), continuing
 * on the neighbouring page at either end. An order opened by a link is looked up once and the list pages
 * to it, so that it is highlighted there and has neighbours.
 */
export function useOrderNavigation(currentOrder: Order | null | undefined) {
  const {
    orders,
    ordersFetching,
    ordersUpdatedAt,
    refetchOrders,
    page,
    perPage,
    setPage,
    totalPages,
    totalOrders,
    locateOrder,
  } = useOrderContext();
  const currentOrderId = currentOrder?.id;
  const [pending, setPending] = useState<PendingOrderMove | null>(null);
  const arriveRef = useRef<((order: Order) => void) | null>(null);
  const countingRef = useRef(false);
  // the pending move a refetch of an outdated page was asked for (once per move)
  const refetchedForRef = useRef<PendingOrderMove | null>(null);

  const index = currentOrderId && orders ? orders.findIndex((o) => o.id === currentOrderId) : -1;
  const onPage = index !== -1 && !!orders;
  const hasPrev = onPage && (index > 0 || page > 0);
  const hasNext =
    onPage && (index < orders!.length - 1 || (totalPages !== undefined && page + 1 < totalPages));
  const position = onPage ? page * perPage + index : null;

  // an order that is not on the loaded page: page the list to it (once per order, the user may page away);
  // not while a move pages the list, nor for the order a move just left (it is still current until the
  // next one is open): both are off the page on purpose, and paging back disabled the arrows meanwhile
  const locatedRef = useRef<string | null>(null);
  const leftRef = useRef<string | null>(null);
  useEffect(() => {
    if (!currentOrder || !orders || leftRef.current === currentOrder.id) return;
    leftRef.current = null;
    if (onPage || pending || locatedRef.current === currentOrder.id) return;
    locatedRef.current = currentOrder.id;
    locateOrder(currentOrder)
      .then((found) => {
        if (found !== null) setPage(Math.floor(found / perPage));
      })
      .catch(() => undefined);
  }, [currentOrder, orders, onPage, pending, locateOrder, perPage, setPage]);

  const move = useCallback(
    (direction: Direction, onArrive: (order: Order) => void) => {
      if (!orders || !currentOrder) return false;
      const idx = orders.findIndex((o) => o.id === currentOrder.id);
      if (idx === -1) return false;
      const target = idx + (direction === "next" ? 1 : -1);
      if (target >= 0 && target < orders.length) {
        onArrive(orders[target]);
        return true;
      }
      const neighbourPage = page + (direction === "next" ? 1 : -1);
      if (neighbourPage < 0 || (totalPages !== undefined && neighbourPage >= totalPages)) return false;
      if (countingRef.current) return true;
      arriveRef.current = onArrive;
      // Past the edge of the page: the page on screen can be minutes old and new orders shift the list
      // meanwhile, so the order's position is counted first (see usePizzaNavigation). "next" looks one
      // place after it, "prev" at its own place, so that the step is taken from it on that page.
      countingRef.current = true;
      const since = Date.now();
      locateOrder(currentOrder, { fresh: true })
        .catch(() => null)
        .then((found) => {
          const located: PendingOrderMove =
            found === null
              ? {
                  direction,
                  fromId: currentOrder.id,
                  page: neighbourPage,
                  fallbackIndex: direction === "next" ? "first" : "last",
                  since: 0,
                }
              : {
                  direction,
                  fromId: currentOrder.id,
                  page: Math.floor((found + (direction === "next" ? 1 : 0)) / perPage),
                  fallbackIndex: (found + (direction === "next" ? 1 : 0)) % perPage,
                  since,
                };
          if (totalPages !== undefined && located.page >= totalPages) return;
          setPending(located);
          setPage(located.page);
        })
        .finally(() => {
          countingRef.current = false;
        });
      return true;
    },
    [orders, currentOrder, page, perPage, totalPages, setPage, locateOrder],
  );

  // finish a move once the neighbouring page is loaded
  useEffect(() => {
    if (!pending) return;
    // another order was opened or the list was paged meanwhile
    if (currentOrderId !== pending.fromId || page !== pending.page) {
      setPending(null);
      return;
    }
    // the page has to be loaded after the position was counted: a cached copy, or the page that was
    // already on screen, can be shifted by new orders (see usePizzaNavigation)
    if (!orders || ordersFetching) return;
    if (ordersUpdatedAt < pending.since && refetchedForRef.current !== pending) {
      refetchedForRef.current = pending;
      refetchOrders();
      return;
    }
    const step = pending.direction === "next" ? 1 : -1;
    const idx = orders.findIndex((o) => o.id === pending.fromId);
    const target =
      idx !== -1
        ? idx + step
        : pending.fallbackIndex === "first"
          ? 0
          : pending.fallbackIndex === "last"
            ? orders.length - 1
            : pending.fallbackIndex;
    if (target < 0 || target >= orders.length) {
      // the neighbour is on the next page over
      const nextPage = page + step;
      if (nextPage < 0 || (totalPages !== undefined && nextPage >= totalPages)) {
        setPending(null);
      } else {
        setPending({ ...pending, page: nextPage, fallbackIndex: step > 0 ? "first" : "last" });
        setPage(nextPage);
      }
      return;
    }
    setPending(null);
    leftRef.current = pending.fromId;
    arriveRef.current?.(orders[target]);
  }, [
    pending,
    orders,
    ordersFetching,
    ordersUpdatedAt,
    refetchOrders,
    page,
    totalPages,
    setPage,
    currentOrderId,
  ]);

  return { hasPrev, hasNext, position, total: totalOrders, move, moving: !!pending };
}
